<template>
  <div class="modal">
    <button class="close-btn" @click="closeModal">✖</button>

    <div class="trade-container">
      <div class="trade-pics" @paste="handlePaste">
        <h2>TRADE PICS</h2>
        <label for="file-upload" class="upload-label"> 📂 Add image </label>
        <input type="file" id="file-upload" @change="handleFileUpload" hidden />

        <div v-if="trade.imageUrls.length > 0" class="image-preview">
          <div
            v-for="(image, index) in trade.imageUrls"
            :key="index"
            class="image-item"
          >
            <img
              :src="image"
              class="trade-img"
              @click="openImage(image)"
              @mouseover="zoomImage($event)"
              @mouseleave="resetZoom($event)"
            />
            <button @click="removeImage(index)">🗑</button>
          </div>
        </div>
      </div>

      <div class="trade-notes">
        <h2>NOTES</h2>
        <textarea
          v-model="trade.note"
          placeholder="Add your comment here..."
        ></textarea>

        <div class="status-selector">
          <label>Statut :</label>
          <select v-model="trade.status">
            <option value="Win">Win</option>
            <option value="Loss">Loss</option>
          </select>
        </div>

        <button @click="saveTrade" :disabled="isUploading">
          {{ isUploading ? "Saving..." : "Saved" }}
        </button>
      </div>
    </div>

    <!-- Modale pour afficher l'image en grand -->
    <div v-if="zoomedImage" class="lightbox" @click="closeImage">
      <img :src="zoomedImage" class="fullsize-img" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { deleteObject, ref as storageRef } from "firebase/storage"; // ✅ Renommé
import { useRouter, useRoute } from "vue-router";
import {
  uploadTradeImages,
  getTrade,
  updateTrade,
  storage,
} from "@/services/firestore";
import { getAuth } from "firebase/auth";

export default defineComponent({
  name: "TradeOptionsPage",
  setup() {
    const auth = getAuth();
    const router = useRouter();
    const route = useRoute();
    const tradeId = route.params.tradeId as string;
    const tradeDate = ref(route.query.date as string | null);
    const userId = auth.currentUser?.uid;

    const isUploading = ref(false);

    const zoomedImage = ref<string | null>(null);

    const openImage = (image: string) => {
      zoomedImage.value = image;
    };

    const closeImage = () => {
      zoomedImage.value = null;
    };

    const zoomImage = (event: MouseEvent) => {
      const img = event.target as HTMLImageElement;
      img.style.transform = "scale(2)";
      img.style.transition = "transform 0.2s ease-in-out";
    };

    const resetZoom = (event: MouseEvent) => {
      const img = event.target as HTMLImageElement;
      img.style.transform = "scale(1)";
    };

    // Trade actuel
    const trade = ref({
      note: "",
      status: "Win" as "Win" | "Loss",
      imageUrls: [] as string[],
    });

    // Charger les données existantes du trade
    const loadTrade = async () => {
      if (!userId) {
        console.error("Utilisateur non authentifié");
        return;
      }
      try {
        const existingTrade = await getTrade(userId, tradeId);
        if (existingTrade) {
          trade.value = existingTrade;
        }
      } catch (error) {
        console.error("Erreur lors du chargement du trade :", error);
      }
    };

    // Gestion de l'upload des fichiers
    const handleFileUpload = async (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0 && userId) {
        isUploading.value = true;
        try {
          const uploadedUrls = await uploadTradeImages(target.files, userId);
          trade.value.imageUrls = [...trade.value.imageUrls, ...uploadedUrls];
        } catch (error) {
          console.error("Erreur lors de l'upload des images :", error);
        }
        isUploading.value = false;
      }
    };

    // Gestion du collage d'une image
    const handlePaste = async (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.indexOf("image") !== -1) {
          const file = item.getAsFile();
          if (file && userId) {
            isUploading.value = true;
            try {
              const uploadedUrls = await uploadTradeImages([file], userId);
              trade.value.imageUrls = [
                ...trade.value.imageUrls,
                ...uploadedUrls,
              ];
            } catch (error) {
              console.error(
                "Erreur lors de l'upload de l'image collée :",
                error
              );
            }
            isUploading.value = false;
          }
        }
      }
    };

    // Supprimer une image
    const removeImage = async (index: number) => {
      if (!userId) {
        console.error("Utilisateur non authentifié");
        return;
      }

      const imageUrl = trade.value.imageUrls[index];
      if (!imageUrl) {
        console.error("URL d'image introuvable");
        return;
      }

      try {
        // Référence à l'image dans Firebase Storage
        const imageRef = storageRef(storage, imageUrl);

        // Suppression de l'image dans Firebase Storage
        await deleteObject(imageRef);
        console.log(`Image supprimée: ${imageUrl}`);

        // Suppression de l'image dans le tableau local
        trade.value.imageUrls.splice(index, 1);

        // Mise à jour du trade dans Firestore
        await updateTrade(userId, tradeId, {
          imageUrls: trade.value.imageUrls,
        });
      } catch (error) {
        console.error("Erreur lors de la suppression de l'image :", error);
      }
    };

    // Sauvegarder le trade
    const saveTrade = async () => {
      if (!userId) {
        console.error("Utilisateur non authentifié");
        return;
      }
      isUploading.value = true;
      try {
        await updateTrade(userId, tradeId, trade.value);
        if (tradeDate.value) {
          router.push(`/trades/${tradeDate.value}`);
        } else {
          console.warn("Date non trouvée, redirection vers /trades");
          router.push("/trades");
        }
      } catch (error) {
        console.error("Erreur lors de l'enregistrement du trade :", error);
      }
      isUploading.value = false;
    };

    // Fermer la fenêtre (retour à la liste de trades)
    const closeModal = () => {
      if (tradeDate.value) {
        router.push(`/trades/${tradeDate.value}`);
      } else {
        console.warn("Date non trouvée, retour au dashboard.");
        router.push("/dashboard");
      }
    };

    onMounted(loadTrade);

    return {
      trade,
      handleFileUpload,
      handlePaste,
      saveTrade,
      removeImage,
      closeModal,
      isUploading,
      zoomedImage,
      openImage,
      closeImage,
      zoomImage,
      resetZoom,
    };
  },
});
</script>

<style scoped>
/* Conteneur principal */
.modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  max-width: 600px;
  background: #1a1a1a;
  padding: 20px;
  border-radius: 15px;
  color: white;
  text-align: left;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.2);
}

/* Bouton de fermeture */
.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: red;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 18px;
  padding: 5px;
}

/* Conteneur de l'édition */
.trade-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Zone d'ajout d'image */
.trade-pics {
  background: #262626;
  padding: 15px;
  border-radius: 10px;
}

.upload-label {
  display: block;
  padding: 10px;
  background: #ffcc00;
  color: black;
  text-align: center;
  cursor: pointer;
  border-radius: 5px;
}

/* Aperçu des images */
.image-preview {
  display: flex;
  gap: 10px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.image-item {
  position: relative;
  display: inline-block;
}

.image-item button {
  position: absolute;
  top: 0;
  right: 0;
  background: red;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  padding: 5px;
}

.trade-img {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 5px;
}

/* Zone de texte pour la note */
.trade-notes {
  background: #333;
  padding: 15px;
  border-radius: 10px;
}

textarea {
  width: 100%;
  height: 100px;
  background: #444;
  color: white;
  padding: 10px;
  border-radius: 5px;
  border: none;
}

/* Sélection du statut */
.status-selector {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}

select {
  padding: 5px;
  border-radius: 5px;
  border: none;
  background: #ffcc00;
  color: black;
}

/* Bouton d'enregistrement */
button {
  display: block;
  width: 100%;
  padding: 10px;
  margin-top: 10px;
  background: green;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

button:disabled {
  background: gray;
}
/* Zoom au survol */
.trade-img {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 5px;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
}

/* Lightbox (modale d'agrandissement de l'image) */
.lightbox {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.fullsize-img {
  max-width: 90%;
  max-height: 90%;
  border-radius: 10px;
}
</style>
